import React from 'react';
import './Privacy.css';

const sections = [
  {
    num: '01',
    title: 'Information We Collect',
    items: [
      { label: 'Account info', text: 'Name, email, and username to create and manage your FlareChat account.' },
      { label: 'Location info', text: 'Used to show nearby flares relevant to your area.' },
      { label: 'Chat data', text: 'Messages stored securely for group conversations.' },
      { label: 'Device data', text: 'IP address, app usage, and analytics to improve the experience.' },
    ]
  },
  {
    num: '02',
    title: 'How We Use Your Data',
    items: [
      { text: 'Provide and improve the FlareChat service.' },
      { text: 'Show flares and chats relevant to you and your location.' },
      { text: 'Prevent fraud, abuse, and security threats.' },
      { text: 'Conduct analytics to improve user experience.' },
    ]
  },
  {
    num: '03',
    title: 'Sharing of Data',
    items: [
      { text: 'We do not sell your personal data.' },
      { text: 'We may share limited data with trusted service providers (hosting, analytics, etc.).' },
      { text: 'We may disclose information if required by law or to protect our rights.' },
    ]
  },
  {
    num: '04',
    title: 'International Data Transfers',
    content: 'If you use FlareChat outside the United States, your data may be transferred and processed in the U.S. in accordance with applicable data protection laws.'
  },
  {
    num: '05',
    title: 'Data Retention & Security',
    items: [
      { text: 'We retain data only as long as necessary for the purposes outlined in this policy.' },
      { text: 'We use encryption and industry-standard security measures to protect your data.' },
    ]
  },
  {
    num: '06',
    title: 'Your Rights',
    items: [
      { text: 'Request a copy of your personal data.' },
      { text: 'Delete your account and associated data at any time.' },
      { text: 'For residents in certain jurisdictions (e.g., GDPR, CCPA), you may have additional rights.' },
    ]
  },
  {
    num: '07',
    title: "Children's Privacy",
    content: "FlareChat is not intended for children under 18. We do not knowingly collect data from children under 18. If you believe a child has provided us with personal information, please contact us immediately."
  },
  {
    num: '08',
    title: 'Child Safety Standards',
    content: 'FlareChat is committed to preventing child sexual abuse and exploitation (CSAE) and addressing child sexual abuse material (CSAM) in accordance with Google Play Child Safety Standards and applicable laws.',
    items: [
      { label: 'Published standards', text: 'We prohibit any content, behavior, grooming, solicitation, exploitation, or sharing of material that sexualizes, harms, or endangers children.' },
      { label: 'User reporting', text: 'Users can report safety concerns, abusive behavior, or suspected CSAE/CSAM through the app or by emailing our safety contact.' },
      { label: 'CSAM response', text: 'We review reported content and accounts, remove violating material, restrict or terminate offending accounts, preserve relevant evidence when appropriate, and report matters to the proper authorities where required by law.' },
      { label: 'Legal compliance', text: 'We comply with applicable child safety, online safety, privacy, and reporting laws and cooperate with lawful requests from regulators and law enforcement.' },
      { label: 'Child Safety Point of Contact', text: 'Jim, FlareChat Child Safety Team, jim@getflarechat.com. This contact is designated to receive Google Play or user notifications about CSAE/CSAM concerns and to speak to our prevention, review, and enforcement procedures.' },
    ]
  },
  {
    num: '09',
    title: 'Changes to this Policy',
    content: 'We may update this Privacy Policy from time to time. Material changes will be communicated in-app or by email. Continued use of FlareChat after changes constitutes acceptance of the updated policy.'
  },
];

export default function Privacy() {
  return (
    <div className="privacy-page">
      {/* Hero */}
      <section className="privacy-hero">
        <div className="privacy-orb" />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="section-tag animate-fade-up" style={{ background: 'rgba(244,97,26,0.2)', color: '#FFAD6F', borderColor: 'rgba(244,97,26,0.35)', margin: '0 auto 20px' }}>
            <span className="dot" style={{ background: '#F5A623' }} />
            Legal
          </div>
          <h1 className="privacy-heading animate-fade-up delay-1">
            Privacy <span className="gradient-text">Policy</span>
          </h1>
          <p className="privacy-sub animate-fade-up delay-2">
            Effective Date: <strong>April 2nd, 2026</strong>
          </p>
          <p className="privacy-intro animate-fade-up delay-3">
            FlareChat values your privacy. This Privacy Policy explains how we collect, use, and protect your information.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="privacy-body">
        <div className="container">
          <div className="privacy-layout">
            {/* TOC */}
            <nav className="privacy-toc">
              <p className="toc-title">Table of Contents</p>
              {sections.map((s) => (
                <a key={s.num} href={`#section-${s.num}`} className="toc-link">
                  <span className="toc-num">{s.num}</span>
                  {s.title}
                </a>
              ))}
              <div className="toc-contact">
                <p>Questions?</p>
                <a href="mailto:jim@getflarechat.com">jim@getflarechat.com</a>
              </div>
            </nav>

            {/* Main content */}
            <div className="privacy-content">
              {sections.map((s) => (
                <div key={s.num} id={`section-${s.num}`} className="privacy-section">
                  <div className="section-num-badge">{s.num}</div>
                  <h2>{s.title}</h2>

                  {s.content && <p className="section-text">{s.content}</p>}

                  {s.items && (
                    <ul className="privacy-list">
                      {s.items.map((item, i) => (
                        <li key={i}>
                          {item.label && <strong>{item.label}: </strong>}
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              <div className="privacy-contact-card">
                <div className="contact-card-icon">📧</div>
                <div>
                  <h3>Questions about this policy?</h3>
                  <p>Reach out to us directly and we'll respond within 48 hours.</p>
                  <a href="mailto:jim@getflarechat.com" className="privacy-email">jim@getflarechat.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
